import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight, Check, Users, Calendar, MapPin, Plane, Bus, UtensilsCrossed,
  ClipboardCheck, Headset, Sparkles, Star
} from "lucide-react";
import { fetchTour } from "@/lib/api";
import { TOUR_DETAIL } from "@/constants/testIds";

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchTour(slug).then(setTour).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="container-edge py-32 text-center">
        <h1 className="display-serif text-4xl mb-4">Tour not found</h1>
        <Link to="/tours" className="btn-secondary inline-flex">Back to tours</Link>
      </div>
    );
  }
  if (!tour) {
    return <div className="container-edge py-32 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/60">Loading itinerary…</div>;
  }

  const categoryAccent = tour.category === "HBCU Tour"
    ? "bg-coral text-cream"
    : tour.category === "Day Tour"
    ? "bg-sun text-ink"
    : tour.category === "Group Travel"
    ? "bg-teal text-cream"
    : "bg-ink text-cream";

  return (
    <>
      <section className="relative h-[78vh] min-h-[520px] flex items-end overflow-hidden" data-testid={TOUR_DETAIL.hero}>
        <img src={tour.hero_image} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-mask" />
        <div className="relative container-edge pb-16 text-cream">
          <span className={`chip ${categoryAccent} mb-5`}>
            <Sparkles size={12} /> {tour.category}
          </span>
          <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
            {tour.title}
          </h1>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-cream/85">{tour.destination}</div>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-cream/90 leading-relaxed">{tour.summary}</p>
        </div>
      </section>

      <section className="bg-cream border-b border-ink/10">
        <div className="container-edge py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <Meta icon={Calendar} label="Duration" value={`${tour.duration_days} ${tour.duration_days === 1 ? "Day" : "Days"}`} />
          <Meta icon={Users} label="Min Group" value={`${tour.min_group_size} Guests`} />
          <Meta icon={MapPin} label="Region" value={tour.destination} />
          <Meta icon={Star} label="Pricing" value={tour.starting_from_label} />
        </div>
      </section>

      {/* Example tour disclaimer */}
      <section className="container-edge pt-8">
        <div className="bg-gold/20 border-2 border-gold/50 rounded-2xl p-5 flex items-start gap-4" data-testid="example-tour-disclaimer">
          <Sparkles size={20} className="text-ink mt-0.5 shrink-0" />
          <div className="text-sm text-ink/85 leading-relaxed">
            <strong>This is a sample itinerary.</strong> Dates, routes and specific colleges/destinations are subject to availability. We cover any college or destination upon request — built around your group's needs.
          </div>
        </div>
        <div className="mt-4 bg-coral/10 border-2 border-coral/40 rounded-2xl p-5 flex items-start gap-4" data-testid="duration-flexibility-note">
          <Calendar size={20} className="text-coral mt-0.5 shrink-0" />
          <div className="text-sm text-ink/85 leading-relaxed">
            {tour.category === "Group Travel" ? (
              <>
                <strong>1 to 7 days — flexible.</strong> KNV Travelz group travel tours run anywhere from a single-day trip up to a seven-day experience. Choose whatever length fits your group; nothing we offer goes beyond seven days.
              </>
            ) : (
              <>
                <strong>1 to 5 days — flexible.</strong> KNV Travelz college and day tours run anywhere from a single-day campus visit up to a five-day multi-school trip. Choose whatever length fits your group; nothing we offer goes beyond five days.
              </>
            )}
          </div>
        </div>
      </section>

      <section className="container-edge py-20 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-7" data-testid={TOUR_DETAIL.itinerary}>
          <div className="eyebrow mb-3">Day by Day</div>
          <h2 className="display-serif text-4xl mb-12">The itinerary</h2>
          <ol className="space-y-8">
            {tour.itinerary.map((d) => (
              <li key={d.day} className="relative pl-14">
                <div className="absolute left-0 top-0 w-11 h-11 rounded-2xl bg-coral text-cream flex items-center justify-center font-display font-semibold text-lg">
                  {d.day}
                </div>
                <div className="bg-white rounded-2xl ring-1 ring-ink/8 p-6">
                  <h3 className="display-serif text-2xl mb-2">{d.title}</h3>
                  <p className="text-muted2 text-sm leading-relaxed">{d.detail}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-20">
            <div className="eyebrow mb-3">Highlights</div>
            <h3 className="display-serif text-3xl mb-6">What makes this trip</h3>
            <ul className="space-y-3">
              {tour.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <Sparkles size={18} className="text-coral mt-0.5 shrink-0" />
                  <span className="text-ink/85">{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-20">
            <div className="eyebrow mb-3">From the field</div>
            <h3 className="display-serif text-3xl mb-6">Moments on trip</h3>
            <div className="grid grid-cols-2 gap-4">
              {tour.gallery.map((src, i) => (
                <div key={src} className={`overflow-hidden rounded-2xl ${i === 0 ? "col-span-2" : ""}`}>
                  <img src={src} alt="" className="w-full h-72 object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="col-span-12 md:col-span-5 md:sticky md:top-28 self-start space-y-6">
          <div className="rounded-3xl bg-ink text-cream p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 bg-coral/20 rounded-full blur-2xl" />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-sun mb-4">The KNV All-Inclusive Promise</div>
              <p className="text-sm text-cream/80 mb-5 leading-relaxed">
                Standard on every KNV tour. No add-ons. No surprises.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: ClipboardCheck, t: "Dedicated tour coordinator" },
                  { icon: Headset, t: "On-trip tour manager" },
                  { icon: Plane, t: "Round-trip group air" },
                  { icon: Bus, t: "Round-trip ground transport" },
                  { icon: UtensilsCrossed, t: "All meals included" },
                  { icon: Sparkles, t: "All activities & admissions" },
                ].map(({ icon: Icon, t }) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-cream/90">
                    <Icon size={18} className="text-sun shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-3xl bg-white p-8 ring-1 ring-ink/8 soft-shadow">
            <div className="eyebrow mb-3">Specific to this trip</div>
            <ul className="space-y-3 mb-6">
              {tour.includes.map((i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Check size={16} className="text-coral mt-0.5 shrink-0" />
                  <span className="text-ink/85">{i}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-ink/8 pt-5">
              <p className="text-sm text-muted2 mb-4 leading-relaxed">
                Ready to talk dates &amp; group size? Custom quote within 2 business days.
              </p>
              <Link
                to={`/request-quote?tour=${tour.slug}`}
                className="btn-primary w-full"
                data-testid={TOUR_DETAIL.requestBtn}
              >
                Plan My Trip <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-2xl bg-coral/10 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-coral" />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55">{label}</div>
        <div className="font-medium text-ink">{value}</div>
      </div>
    </div>
  );
}
